<script setup>
import { computed, h, ref } from "vue";
import _cloneDeep from "lodash/cloneDeep";
import { useI18n } from "vue-i18n";
import { UserIcon } from "lucide-vue-next";
import { Modal } from "@Components/Modal";
import { FormTextarea } from "@Components/Form";

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    profiles: {
        type: Array,
        default: [],
    },
});

const emit = defineEmits(["update:modelValue"]);

const { t: __ } = useI18n();

const selectProfileModal = ref(null);

const formattedProfiles = computed(() => {
    return props.profiles.map((p) => {
        return {
            id: p.id,
            icon: h("img", { src: p.user_avatar_url, class: "size-8 rounded-full" }),
            name: p.user_full_name,
            job: p.job,
            custom_instructions: p.custom_instructions,
            user_id: p.user_id,
        };
    });
});

const selectedProfile = computed(() => {
    return formattedProfiles.value.find((p) => p.id === props.modelValue.persona_id);
});

const selectProfile = (profile) => {
    if (profile === null) {
        props.modelValue.persona_id = null;
        props.modelValue.modules.wordpress.author_id = null;
    } else {
        props.modelValue.persona_id = profile.id;
        props.modelValue.modules.wordpress.author_id = profile.user_id;
    }

    selectProfileModal.value.hide();
};
</script>

<template>
    <div class="space-y-6">
        <div>
            <div class="block text-sm font-medium leading-6 text-gray-900 dark:text-white">{{ __("Profil") }}</div>

            <div class="mt-2">
                <div v-if="formattedProfiles.length > 0">
                    <button
                        v-if="selectedProfile"
                        @click.prevent="$refs.selectProfileModal.show()"
                        :class="[
                            'w-full relative flex p-4 space-x-6 text-left rounded-lg border-0 ring-1 ring-inset cursor-pointer group focus:ring-2 focus:ring-inset focus:outline-none items-center',
                            'bg-white ring-gray-300 focus:ring-gray-950',
                            'dark:bg-gray-900 dark:ring-gray-800 dark:focus:ring-gray-700',
                        ]">
                        <Component v-if="selectedProfile.icon" :is="selectedProfile.icon" class="size-8 flex-none" />

                        <div class="flex-1">
                            <div class="text-sm font-medium text-gray-900 dark:text-white">
                                {{ selectedProfile.name }}
                            </div>
                            <div class="flex items-center mt-1 text-sm text-gray-600 dark:text-gray-400">
                                {{ selectedProfile.job }}
                            </div>
                        </div>
                    </button>
                    <button
                        v-else
                        @click.prevent="$refs.selectProfileModal.show()"
                        :class="[
                            'w-full relative flex p-4 space-x-6 text-left rounded-lg border-0 ring-1 ring-inset cursor-pointer group focus:ring-2 focus:ring-inset focus:outline-none items-center',
                            'bg-white ring-gray-300 focus:ring-gray-950',
                            'dark:bg-gray-900 dark:ring-gray-800 dark:focus:ring-gray-700',
                        ]">
                        <UserIcon class="size-8 flex-none text-gray-900 dark:text-white" />

                        <div class="flex flex-1 items-center">
                            <div class="text-sm font-medium text-gray-900 dark:text-white">
                                {{ __("Sélectionner un profil") }}
                            </div>
                        </div>
                    </button>
                </div>
                <div v-else class="italic text-gray-600 dark:text-gray-400">
                    {{ __("Aucun profil") }}
                </div>
            </div>

            <Modal ref="selectProfileModal" class="overflow-hidden sm:max-w-3xl">
                <div class="max-h-[80vh] min-h-0 flex flex-col flex-1 py-6">
                    <div class="px-4 lg:px-8 sm:px-6">
                        <div class="block text-sm font-medium leading-6 text-gray-900 dark:text-white">{{ __("Sélectionner un profil") }}</div>
                    </div>
                    <div class="overflow-y-auto px-4 mt-6 lg:px-8 sm:px-6">
                        <div v-if="formattedProfiles.length > 0" class="grid gap-3 sm:grid-cols-2">
                            <button
                                v-for="profile in formattedProfiles"
                                :key="profile.id"
                                @click.prevent="selectProfile(profile)"
                                :class="[
                                    'w-full relative flex p-4 space-x-6 text-left rounded-lg border-0 ring-1 ring-inset cursor-pointer group focus:ring-2 focus:ring-inset focus:outline-none items-center',
                                    'bg-white ring-gray-300 focus:ring-gray-950',
                                    'dark:bg-gray-800 dark:ring-gray-700 dark:focus:ring-gray-600',
                                ]">
                                <Component v-if="profile.icon" :is="profile.icon" class="size-8 flex-none" />

                                <div class="flex-1">
                                    <div class="text-sm font-medium text-gray-900 dark:text-white">
                                        {{ profile.name }}
                                    </div>
                                    <div class="flex items-center mt-1 text-sm text-gray-600 dark:text-gray-400">
                                        {{ profile.job }}
                                    </div>
                                </div>
                            </button>
                            <button
                                @click.prevent="selectProfile(null)"
                                :class="[
                                    'w-full relative flex p-4 space-x-6 text-left rounded-lg border-0 ring-1 ring-inset cursor-pointer group focus:ring-2 focus:ring-inset focus:outline-none items-center',
                                    'bg-white ring-gray-300 focus:ring-gray-950',
                                    'dark:bg-gray-800 dark:ring-gray-700 dark:focus:ring-gray-600',
                                ]">
                                <UserIcon class="size-8 flex-none text-gray-900 dark:text-white" />

                                <div class="flex flex-1 items-center">
                                    <div class="text-sm font-medium text-gray-900 dark:text-white">
                                        {{ __("Aucun profil") }}
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>

        <FormTextarea
            v-model="modelValue.modules.content.custom_instructions.profile"
            :label="__('Instructions personnalisées pour le profil')"
            :placeholder="
                selectedProfile?.custom_instructions ||
                __(
                    'Exemple : Tu es un expert en organisation de voyages, spécialisé dans la planification sur mesure en fonction des préférences des voyageurs. Ton rôle est de proposer des itinéraires détaillés, en tenant compte du budget, des centres d\'intérêt, des meilleures périodes pour voyager, ainsi que des options de transport et d\'hébergement les plus adaptées.'
                )
            "
            :rows="5"
            :left-actions="[
                {
                    id: 'magic-prompt',
                },
            ]"
            :right-actions="[
                {
                    id: 'maximize',
                },
            ]">
        </FormTextarea>
    </div>
</template>
